import { HttpClient, HttpHeaders } from "@angular/common/http";
import { DOCUMENT } from "@angular/common";
import { computed, effect, inject, Injectable, NgZone, signal } from "@angular/core";
import { Config, HttpClient as ForgerockHttpClient, FRUser, TokenManager } from "@forgerock/javascript-sdk";
import { firstValueFrom, lastValueFrom, take, type Observable } from "rxjs";
import { environment } from "@environments/environment";
import { UserService } from "@modules/user-management/_services/user.service";
import { SetAuthenticatedSessionUser, UpdateUserData, UpdateUserRole } from "@store/actions/userData.action";
import { AppState } from "@store/models/state.model";
import { Store } from "@ngrx/store";
import { DeepLinkService } from "@shared/services/deeplink/deeplink.service";
import { getUserDataState } from "@store/index";
import { areStringArraysEqual, shallowEqual } from "@shared/utils/array-helpers.util";
import { TabStateService } from "@shared/services/tab-state/tab-state.service";
import { User } from "@modules/user-management/_models/user-management-api.interface";

type LoginType = "embedded" | "redirect" | undefined;

/**
 * Minimum time between two background token renewals. Ping ends a session after a period of idle
 * time and only requests that reach Ping count as activity; calls to our APIs with a valid bearer
 * token never do. Renewing on user activity, at most this often, keeps an active user signed in
 * while an idle tab still expires on Ping's schedule.
 */
export const KEEP_ALIVE_AFTER_MS = 5 * 60_000;

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

@Injectable({
	providedIn: "root"
})
export class AuthService {
	private http = inject(HttpClient);
	private userService = inject(UserService);
	private store = inject(Store<AppState>);
	private deepLinkService = inject(DeepLinkService);
	private tabStateService = inject(TabStateService);
	private document = inject(DOCUMENT);
	private ngZone = inject(NgZone);

	private lastRenewedAt = 0;
	private renewal: Promise<boolean> | null = null;
	private keepAliveStarted = false;
	private sessionGeneration = 0;

	private readonly clientId = "CloudPayUI";

	private accessToken = signal<string | null>(null);
	private refreshToken = signal<string | null>(null);
	private idToken = signal<string | null>(null);

	public userTokens = computed(() => {
		return {
			accessToken: this.accessToken(),
			refreshToken: this.refreshToken(),
			idToken: this.idToken()
		};
	});

	constructor() {
		effect(onCleanup => {
			if (this.userTokens().accessToken) {
				const generation = this.sessionGeneration;
				const subscription = this.userService.getUserDetails().subscribe(userDetail => {
					if (generation !== this.sessionGeneration) return;
					this.restoreUserState(userDetail);
					this.store.dispatch(
						new UpdateUserRole({
							userData: { roles: userDetail.roles }
						})
					);
				});
				onCleanup(() => subscription.unsubscribe());
			}
		});
	}

	private restoreUserState(user: User): void {
		const userId = user.id ? `id:${user.id}` : user.email ? `email:${user.email}` : "";
		this.store.dispatch(new SetAuthenticatedSessionUser(userId));
	}

	/** Starts renewing the tokens in the background while the user is active. Safe to call more than once. */
	public startKeepAlive(): void {
		if (this.keepAliveStarted) return;
		this.keepAliveStarted = true;

		// Outside the zone so a mouse or keyboard event does not trigger change detection
		this.ngZone.runOutsideAngular(() => {
			for (const type of ACTIVITY_EVENTS) {
				this.document.addEventListener(type, () => this.keepAlive(), {
					capture: true,
					passive: true
				});
			}
		});
	}

	/** Renews the tokens if the user is signed in and the last renewal is older than KEEP_ALIVE_AFTER_MS. */
	public keepAlive(now = Date.now()): void {
		if (!this.accessToken() || this.renewal || now - this.lastRenewedAt < KEEP_ALIVE_AFTER_MS) return;
		// Set before the renewal settles so a burst of clicks starts one renewal, not one each
		this.lastRenewedAt = now;
		void this.renewTokens();
	}

	/**
	 * Silently renews the tokens without ever redirecting to the login page. Single-flight: concurrent
	 * callers (keep-alive, several 401s) share one renewal. A failed renewal keeps the stored tokens.
	 */
	public renewTokens(): Promise<boolean> {
		this.renewal ??= this.validateTokens(true, undefined, false).finally(() => {
			this.renewal = null;
		});
		return this.renewal;
	}

	public bootstrapAuthClient() {
		Config.set({
			clientId: this.clientId,
			redirectUri: `${window.location.origin}/callback`, // where to redirect after login
			scope: "openid fr:idm:* apps",
			serverConfig: {
				baseUrl: `${environment.pingIdentity.authUrl}/am`,
				timeout: 3000
			},
			realmPath: "alpha"
		});
	}

	/**
	 * Validates the user's session and fetches user details if necessary.
	 * @param renew - Whether to renew the tokens if they are not valid.
	 * @returns A Promise that resolves to true if the token is valid, false otherwise.
	 */
	public async validateUserSession(renew: boolean) {
		const generation = this.sessionGeneration;
		try {
			const tokensValid = await this.validateTokens(renew, "redirect", false);
			if (!tokensValid || generation !== this.sessionGeneration) return false;

			if (this.accessToken() && this.userService.roles?.length) {
				const userDetails = await lastValueFrom(this.userService.getUserDetails());
				if (generation !== this.sessionGeneration) return false;
				this.restoreUserState(userDetails);
				this.store.dispatch(
					new UpdateUserRole({
						userData: { roles: userDetails.roles }
					})
				);

				// User is already authenticated in-memory; still consume any preserved deep link.
				this.deepLinkService.handlePostAuthDeepLink();
				return true;
			}

			const headers = {
				"Accept-API-Version": "protocol=1.0,resource=2.0"
			};

			const { valid, uid } = await firstValueFrom(
				this.accessManagementRequest("sessions", "POST", headers, {
					_action: "validate"
				})
			);

			if (generation !== this.sessionGeneration) return false;
			if (valid) {
				if ((!this.accessToken() || !this.userService.roles?.length) && uid) {
					const userDetails = await lastValueFrom(this.userService.getUserDetails());
					if (generation !== this.sessionGeneration) return false;
					this.restoreUserState(userDetails);
					const userDataPayload = {
						firstName: userDetails.firstName || "",
						lastName: userDetails.lastName || "",
						email: userDetails.email || "",
						initials: `${userDetails.firstName?.[0] ?? ""}${userDetails.lastName?.[0] ?? ""}`,
						isForgeRock: true,
						roles: userDetails.roles
					};

					// Get current user data once
					const currentUser = await lastValueFrom(this.store.select(getUserDataState).pipe(take(1)));
					if (generation !== this.sessionGeneration) return false;

					// ---- Compare relevant substates ----
					const rolesChanged = !areStringArraysEqual(currentUser.roles, userDetails.roles);

					const userDataChanged = !shallowEqual(
						{
							firstName: currentUser.firstName,
							lastName: currentUser.lastName,
							email: currentUser.email,
							initials: currentUser.initials,
							isForgeRock: currentUser.isForgeRock,
							roles: currentUser.roles
						},
						userDataPayload
					);

					// ---- Dispatch only if there are changes ----
					if (rolesChanged) {
						this.store.dispatch(
							new UpdateUserRole({
								userData: { roles: userDetails.roles }
							})
						);
					}

					if (userDataChanged) {
						this.store.dispatch(
							new UpdateUserData({
								userData: userDataPayload
							})
						);
					}
				}

				// User is now fully authenticated - handle deep links
				this.deepLinkService.handlePostAuthDeepLink();

				return true;
			} else {
				this.clearTokenSignals();
				console.warn("Invalid Session");
				return false;
			}
		} catch (error) {
			console.error("Error validating  ", error);
			throw error;
		}
	}

	/**
	 * Sends an HTTP request to the Access Management API.
	 * @param resource - Path of the AM API resource.
	 * @param queryParams - Query parameters
	 * @param method - HTTP method to use.
	 *
	 */
	public accessManagementRequest(resource: string, method: string, headers: {}, params?: {}, body?: {}) {
		const url = `${environment.pingIdentity.authUrl}/am/json/${resource}`;

		const httpHeaders = new HttpHeaders({
			Accept: "application/json, text/plain, */*",
			...headers
		});

		return this.http.request(method, url, {
			headers: httpHeaders,
			params,
			withCredentials: true,
			body
		}) as Observable<Record<string, any>>;
	}

	/**
	 * Validates the user's tokens by checking if they are still valid and optionally renewing them.
	 * @param renew - Whether to renew the tokens if they are not valid.
	 * @param login - Specifies the type of login: 'embedded' or 'redirect' or undefined.
	 * @param skipBackgroundRequest - If true, skips the background request to get tokens without redirect
	 * @returns A Promise that resolves to true if the tokens are valid, false otherwise.
	 */
	public async validateTokens(renew: boolean, login: LoginType, skipBackgroundRequest: boolean): Promise<boolean> {
		const generation = this.sessionGeneration;
		try {
			const tokens = await TokenManager.getTokens({
				forceRenew: renew,
				login,
				skipBackgroundRequest
			});

			if (!tokens || generation !== this.sessionGeneration) return false;
			// Only a forced renewal (or the first sign-in) reaches Ping; a cached-token check must not postpone keep-alive
			if (renew || this.lastRenewedAt === 0) this.lastRenewedAt = Date.now();
			this.accessToken.set(tokens.accessToken || null);
			this.refreshToken.set(tokens.refreshToken || null);
			this.idToken.set(tokens.idToken || null);

			return !!(tokens && tokens.accessToken && tokens.idToken);
		} catch (err) {
			return false;
		}
	}

	/**
	 * Handles the authentication callback from the identity provider.
	 *
	 * @param code Challenge code (PKCE) returned by the identity provider after user authentication.
	 * @param state To protect against CSRF attacks.
	 */
	public async handleAuthCallback(code: string, state: string) {
		let tries = 0;
		while (tries < 2) {
			try {
				const tokens = await TokenManager.getTokens({ query: { code, state } });
				if (!tokens) throw new Error("No access token returned");

				return true;
			} catch (error) {
				console.warn(`Cant get access token after ${tries} attempt(s) ${error}`);
				tries++;
			}
			await new Promise(r => setTimeout(r, 1000));
		}
		console.info(`Failed to get access token after ${tries} attempts, trying to validate tokens`);
		return this.validateTokens(false, "redirect", false);
	}

	public async logout(preserveRequestedRoute = false) {
		this.sessionGeneration++;
		this.clearTokenSignals();
		this.userService.clearUserDetailsCache();
		this.tabStateService.clearRouteState();
		this.clearLocalStorageExcept(["gd-", "tab-"]);
		this.clearAllCookies();
		Object.keys(sessionStorage)
			.filter(key => key !== "tabIdentifier" && (!preserveRequestedRoute || key !== "requestedRoute"))
			.forEach(key => sessionStorage.removeItem(key));
		await FRUser.logout();
		this.validateUserSession(true);
	}

	private clearLocalStorageExcept(prefixes: string[]) {
		const keysToRemove: string[] = [];

		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && !prefixes.some(prefix => key.startsWith(prefix))) {
				keysToRemove.push(key);
			}
		}

		keysToRemove.forEach(key => localStorage.removeItem(key));
	}

	private clearAllCookies(): void {
		const cookies = document.cookie.split(";");
		for (const cookie of cookies) {
			const eqPos = cookie.indexOf("=");
			const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
			document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
		}
	}

	private clearTokenSignals(): void {
		this.accessToken.set(null);
		this.refreshToken.set(null);
		this.idToken.set(null);
	}

	public async testSession() {
		let minutesToExpiry;
		const headers = {
			"Accept-API-Version": "protocol=1.0,resource=2.0"
		};
		try {
			const { maxIdleExpirationTime } = await firstValueFrom(
				this.accessManagementRequest("sessions", "POST", headers, {
					_action: "getSessionInfo"
				})
			);
			const expiry = Date.parse(maxIdleExpirationTime);
			// Use the cieling value - once the session is actually expired we will be getting auth errors anyway
			minutesToExpiry = Math.ceil((expiry - Date.now()) / (1000 * 60));
		} catch (err: any) {
			const { error } = err;
			if (error.status !== 401) {
				throw err;
			} else {
				minutesToExpiry = 0;
			}
		}
		return minutesToExpiry;
	}
}
